import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../common/types';
import { SMLContainer } from '../../smartLinks/smlContainer/SMLContainer';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';
import { BillingInterval, MUSIC24_PLANS, Music24Plan, Music24PlanId } from '../config/music24StripePlans';
import { parseCheckoutIntent } from '../config/checkoutIntent';
import { useMusic24Checkout } from '../components/SMLPricing/hooks/useMusic24Checkout';
import { PricingPlansStep } from '../components/SMLPricing/components/PricingPlansStep';
import { PlansHead } from './PlansHead';
import { safeReturnPath } from './safeReturnPath';

/**
 * The plans page `/app/plans` (design plans-page a). A full page in the app shell, not a modal:
 * the sidebar stays and the browser Back button works. `?plan=&interval=` comes from a checkout
 * intent (ruling C2): that card is highlighted and that interval is on. `?from=` is the page that
 * opened the plans; Back goes there.
 */
const PlansViewComponent: React.FC = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const checkout = useMusic24Checkout();
  // A visitor who followed a priced link lands on that interval; everyone else on Monthly (QA
  // finding F05: a first purchase must show the monthly price that the site promised).
  const [interval, setInterval] = useState<BillingInterval>(() => parseCheckoutIntent(search)?.interval ?? 'monthly');
  const [highlightedPlanId] = useState<Music24PlanId | null>(
    () => MUSIC24_PLANS.find((plan) => plan.id === parseCheckoutIntent(search)?.plan)?.id ?? null
  );

  const subscribe = useCallback(
    (plan: Music24Plan) => {
      checkout.subscribe(plan, interval).catch(console.error);
    },
    [checkout, interval]
  );

  const goBack = useCallback(() => {
    const from = safeReturnPath(new URLSearchParams(search).get('from'));
    // eslint-disable-next-line no-void
    void navigate(from ?? `/${SMLRouteNames.home}`);
  }, [navigate, search]);

  return (
    <SMLContainer header={<PlansHead interval={interval} onIntervalChange={setInterval} onBack={goBack} />}>
      <PricingPlansStep
        interval={interval}
        loadingPlanId={checkout.loadingPlanId}
        onSubscribe={subscribe}
        highlightedPlanId={highlightedPlanId}
      />
    </SMLContainer>
  );
};

export const PlansView = TypedMemo(PlansViewComponent);
